import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, ExternalLink, Users, Briefcase, Heart } from 'lucide-react';
import SEO from '../components/SEO';

const IndividualFamily = () => {
  return (
    <div className="individual-family-page">
      <SEO 
        title="Individual & Family Health Insurance"
        description="Affordable individual and family health insurance plans in Michigan. Compare marketplace plans, get ACA subsidies help, and find the right coverage for your family. Expert guidance available."
        keywords="individual health insurance, family health insurance, ACA plans, marketplace insurance, health insurance Michigan, affordable health coverage, health insurance quotes"
        path="/individual-family"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-teal-50 to-blue-50 py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <img 
                src="/images/agent-helping-family.png" 
                alt="Individual and Family Health Insurance" 
                className="rounded-lg shadow-xl w-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Individual & Family Health Insurance</h1>
              <p className="text-xl text-gray-700 mb-6">
                Affordable ACA/Marketplace Plans for You and Your Family
              </p>
              <p className="text-lg text-gray-600">
                Turning 26 | Self-Employed | Losing Coverage
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who Needs Coverage Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Who Needs Individual or Family Coverage?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <a href="#ready-to-find" className="block group">
              <Card className="border-4 border-teal-300 bg-teal-50 hover:shadow-2xl transition-all transform hover:-translate-y-2 h-full cursor-pointer">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-teal-700 transition-colors">Turning 26</h3>
                  <p className="text-lg text-gray-800 font-semibold">Aging off your parent's health insurance plan? We'll help you find affordable coverage.</p>
                </CardContent>
              </Card>
            </a>

            <a href="#ready-to-find" className="block group">
              <Card className="border-4 border-blue-300 bg-blue-50 hover:shadow-2xl transition-all transform hover:-translate-y-2 h-full cursor-pointer">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-blue-700 transition-colors">Self-Employed</h3>
                  <p className="text-lg text-gray-800 font-semibold">Running your own business? Get quality health insurance without employer benefits.</p>
                </CardContent>
              </Card>
            </a>

            <a href="#ready-to-find" className="block group">
              <Card className="border-4 border-purple-300 bg-purple-50 hover:shadow-2xl transition-all transform hover:-translate-y-2 h-full cursor-pointer">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-purple-700 transition-colors">Losing Coverage</h3>
                  <p className="text-lg text-gray-800 font-semibold">Job change or life event? You qualify for a Special Enrollment Period.</p>
                </CardContent>
              </Card>
            </a>
          </div>
        </div>
      </section>

      {/* ACA Marketplace Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">Affordable Care Act (ACA) Marketplace Plans</h2>
          <p className="text-center text-lg text-gray-700 mb-12">
            Also known as "Obamacare," these plans offer comprehensive coverage with financial assistance available based on income.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-teal-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-teal-600 mb-4">What's Covered?</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Doctor visits and preventive care</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Hospital stays and emergency services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Prescription drugs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Mental health and substance abuse services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Maternity and newborn care</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Pediatric services including dental and vision</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">Financial Assistance</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span><strong>Premium Tax Credits:</strong> Lower your monthly premium</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span><strong>Cost-Sharing Reductions:</strong> Lower deductibles and copays</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span><strong>Based on Income:</strong> Many families qualify for significant savings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>Plans available at Bronze, Silver, Gold, and Platinum levels</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Learn More Button #1 - After ACA Section */}
          <div className="text-center">
            <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white text-xl py-6 px-10">
              <Link to="/learn-more">Learn More About ACA</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Enrollment Periods */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">When Can You Enroll?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-gradient-to-br from-teal-50 to-white border-2 border-teal-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-teal-600 mb-4">Open Enrollment Period</h3>
                <p className="text-lg text-gray-700 mb-4">November 1 - January 15</p>
                <p className="text-gray-600">
                  The annual period when anyone can enroll in or change their health insurance plan.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">Special Enrollment Period</h3>
                <p className="text-lg text-gray-700 mb-4">Qualifying Life Events</p>
                <p className="text-gray-600">
                  Lost coverage, moved, got married, had a baby, or other qualifying events? You may enroll anytime!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Get Started Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">Ready to Find Your Perfect Plan?</h2>
          
          <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
            <h3 className="text-2xl font-semibold text-center mb-6 text-gray-900">Get Started in 3 Easy Steps:</h3>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Complete Our Confidential Survey</h4>
                  <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white">
                    <a href="https://app.retireflo.com/paulette-%7C-insurance-for-individuals-&-families" target="_blank" rel="noopener noreferrer">
                      Start Survey <ExternalLink className="ml-2" size={16} />
                    </a>
                  </Button>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Or Shop Plans Directly</h4>
                  <Button asChild variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50">
                    <a href="https://www.healthsherpa.com/?_agent_id=paulette-abdullah" target="_blank" rel="noopener noreferrer">
                      Shop Plans <ExternalLink className="ml-2" size={16} />
                    </a>
                  </Button>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Schedule a Consultation</h4>
                  <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
                    <a href="https://calendly.com/llplans/30min" target="_blank" rel="noopener noreferrer">
                      Schedule Appointment <ExternalLink className="ml-2" size={16} />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center space-y-4">
            <p className="text-lg text-gray-700 font-semibold">Prefer to Talk? We're Here to Help!</p>
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-xl px-8 py-6">
              <a href="tel:734.209.6712">
                <Phone className="mr-2" />
                Call: 734.209.6712
              </a>
            </Button>
            <div className="flex items-center justify-center gap-2 text-gray-700">
              <Mail size={20} />
              <span>Email 24/7: <a href="mailto:Paulette@llplans.com" className="text-teal-600 hover:text-teal-700">Paulette@llplans.com</a></span>
            </div>
          </div>

          {/* Learn More Section */}
          <div className="mt-12 bg-blue-50 rounded-lg shadow-lg p-8 border-4 border-blue-400">
            <h3 className="text-3xl font-bold mb-4 text-center text-gray-900">IMPORTANT HEALTH INSURANCE GUIDE</h3>
            <p className="text-xl text-center text-gray-800 mb-6 font-semibold">
              Visit our comprehensive guide to understand all your health insurance options
            </p>
            <div className="text-center">
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-2xl px-12 py-8">
                <Link to="/learn-more">
                  LEARN MORE <ExternalLink className="ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndividualFamily;